import React, {Fragment, useEffect, useReducer, useState} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ai from "../../helpers/axios";
import ReactModal from "react-modal";
import { toast } from "react-toastify";
import NumberFormat from "react-number-format";
import OperatorAutocomplete from "../opi/operator-autocomplete";
import TextAreaInput from "../textarea-input";
import Select from "react-select";
import AircraftAutocomplete from "../aircrafts/aircraft-autocomplete";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "closeModal": {
      return {
        ...state,
        showModal: false,
        operator: null,
        aircraft: null,
        clientType: null,
        notes: "",
        expectedCommission: "",
      };
    }
    case "openModal": {
      return {
        ...state,
        showModal: true,
        ...(action.payload || {}),
      };
    }
    default:
      return state;
  }
};

const PassthroughModule = ({
  tripId,
  setUnsavedChanges,
  parentDispatch,
  mode = "create",
  passthroughData,
  readOnly = false,
}) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const isEdit = mode === "edit";

  const initialState = {
    showModal: false,
    operator: null,
    aircraft: null,
    clientType: null,
    notes: "",
    expectedCommission: "",
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    setUnsavedChanges?.({
      section: 'passthroughModal',
      value: !!state.showModal
    });
  }, [setUnsavedChanges, state.showModal]);

  const handlePassthroughButton = () => {
    dispatch({
      type: "openModal",
      payload: isEdit
        ? {
            operator: passthroughData?.operator || null,
            aircraft: passthroughData?.aircraft ? {
              ...passthroughData.aircraft,
              value: passthroughData.aircraft.id,
              label: passthroughData.aircraft.name,
            } : null,
            clientType: passthroughData?.clientType || null,
            notes: passthroughData?.notes || "",
            expectedCommission: passthroughData?.expectedCommission || "",
          }
        : {},
    });
  };

  const handleOperatorChange = (selectedOperator) => {
    if (selectedOperator) {
      dispatch({
        type: "fieldChanged",
        field: "operator",
        value: {
          ...selectedOperator,
          id: selectedOperator._id,
        },
      });
    } else {
      resetSelection("operator");
    }
  };

  const handleAircraftChange = async (selectedAircraft) => {
    if (selectedAircraft) {
      dispatch({
        type: "fieldChanged",
        field: "aircraft",
        value: {
          ...selectedAircraft,
          id: selectedAircraft.value,
          name: selectedAircraft.label,
        },
      });
    } else {
      resetSelection("aircraft");
    }
  };

  const resetSelection = (fieldName) => {
    setTimeout(() => {
      dispatch({
        type: "fieldChanged",
        field: fieldName,
        value: null,
      });
    }, 0);
  };

  const handleNotesChange = (e) => {
    dispatch({
      type: "fieldChanged",
      field: "notes",
      value: e.target.value,
    });
  };

  const handleCommissionChange = (values) => {
    dispatch({
      type: "fieldChanged",
      field: "expectedCommission",
      value: values.value,
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!state.clientType || !state.operator || !state.aircraft || !state.expectedCommission) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions
      );
    }

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    try {
      const endpoint = isEdit
        ? `/api/trips/${tripId}/pt/edit`
        : `/api/trips/${tripId}/pt`;

      const response = await ai.auth(token).patch(endpoint, {
        clientType: state.clientType,
        operator: state.operator,
        aircraft: state.aircraft,
        notes: state.notes,
        expectedCommission: state.expectedCommission,
      });

      if (response?.data) {
        toast.success(
          isEdit
            ? "Passthrough data successfully updated"
            : "Trip successfully converted to passthrough",
          toastOptions
        );
        dispatch({ type: "closeModal" });

        parentDispatch?.({
          type: "fieldChanged",
          field: "refreshDataToken",
          value: Date.now(),
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An error occurred",
        toastOptions
      );
    }
  };

  return (
    <div id="passthrough-module mt-20">
      {!readOnly && (
        <button
          onClick={handlePassthroughButton}
          className={`base-button evo-blue ${isEdit ? 'icon-button icon-button-small' : 'mt-30'}`}
          title={isEdit ? 'Edit' : 'Convert to Passthrough'}
        >
          {isEdit ? <span className="material-icons">edit</span> : "Convert to Passthrough"}
        </button>
      )}

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={() => dispatch({ type: "closeModal" })}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            overflowY: "scroll",
            maxHeight: "100vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>{isEdit ? "Edit Passthrough Data" : "Convert Trip to Passthrough"}</h4>
        </div>
        <div className="modal-body base-modal overflow-scroll">
          <form onSubmit={handleFormSubmit} className="w-100 base-form mt-20">
            <div className="form-group">
              <label>
                Client Type <span className="required">*</span>
              </label>
              <Select
                className="clientType-select"
                classNamePrefix="select"
                options={[
                  {
                    label: "New",
                    value: "New",
                  },
                  {
                    label: "Repeat",
                    value: "Repeat",
                  },
                  {
                    label: "Personal",
                    value: "Personal",
                  },
                ]}
                placeholder="Client Type"
                value={
                  state.clientType
                    ? { label: state.clientType, value: state.clientType }
                    : null
                }
                onChange={(selectedValue) => {
                  dispatch({
                    type: "fieldChanged",
                    field: "clientType",
                    value: selectedValue.value,
                  });
                }}
                key={`key-client-type`}
                name="clientType"
              />
            </div>

            <OperatorAutocomplete
              required
              handleChange={handleOperatorChange}
              selectedOperator={state.operator}
              label="Operator"
              placeholder="Select an Operator"
              parentDispatch={dispatch}
            />

            <AircraftAutocomplete
              required
              handleChange={handleAircraftChange}
              selectedAircraft={state.aircraft}
              label="Aircraft"
              placeholder="Select an Aircraft"
              parentDispatch={dispatch}
            />

            <TextAreaInput
              name="notes"
              label="Notes"
              value={state.notes}
              handleChange={handleNotesChange}
              required
            />

            <div className="form-group">
              <label>
                Expected Commission <span className="required">*</span>
              </label>
              <NumberFormat
                value={state.expectedCommission}
                thousandSeparator={true}
                prefix={"$"}
                onValueChange={handleCommissionChange}
                placeholder="Expected Commission"
                className="semibold-text"
                decimalScale={2}
                required
              />
            </div>

            <button className="base-button evo-blue w-100 mt-20" type="submit">
              {isEdit ? "Save" : "Convert to Passthrough"}
            </button>

            <button
              className="base-button btn-danger w-100 mt-20"
              type="button"
              onClick={() => dispatch({ type: "closeModal" })}
            >
              Cancel
            </button>
          </form>
        </div>
      </ReactModal>
    </div>
  );
};

export default PassthroughModule;
